import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  listAdminSupportPaymentsForUser,
  getAdminSupportPaymentDetail,
  adminSupportIssueRefundForBooking,
  adminSupportIssueRefundForRequest,
} from "../../../../api/adminSupportApi";

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

/* --------------------- utils --------------------- */
function safeStr(x) {
  return x == null ? "" : String(x).trim();
}
function up(x) {
  return safeStr(x).toUpperCase();
}

function timeAgo(date) {
  const ms = Date.now() - date.getTime();
  if (!Number.isFinite(ms)) return "";
  const sec = Math.floor(ms / 1000);
  if (sec < 30) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
  const mon = Math.floor(day / 30);
  if (mon < 12) return `${mon} month${mon === 1 ? "" : "s"} ago`;
  const yr = Math.floor(mon / 12);
  return `${yr} year${yr === 1 ? "" : "s"} ago`;
}

function fmtDT(x) {
  if (!x) return { pretty: "—", rel: "" };
  const d = new Date(x);
  if (Number.isNaN(d.getTime())) return { pretty: String(x), rel: "" };
  const pretty = new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
  return { pretty, rel: timeAgo(d) };
}

function fmtINR(amount) {
  if (amount == null || amount === "") return "—";
  const n = Number(amount);
  if (!Number.isFinite(n)) return String(amount);
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(n);
  } catch {
    return `₹${n.toFixed(2)}`;
  }
}

function fmtMoney(amount) {
  if (amount == null || amount === "") return "—";
  const n = Number(amount);
  if (!Number.isFinite(n)) return String(amount);
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);
}

function copyText(x) {
  const v = String(x || "");
  if (!v) return;
  try {
    navigator.clipboard?.writeText(v);
  } catch {}
}

function getAdminIdBestEffort() {
  try {
    const direct = localStorage.getItem("adminId");
    if (direct && String(direct).trim()) return direct;

    const raw =
      localStorage.getItem("admin_session") ||
      localStorage.getItem("adminSession") ||
      localStorage.getItem("ADMIN_SESSION") ||
      localStorage.getItem("session_admin");

    if (!raw) return undefined;
    const obj = JSON.parse(raw);
    return obj?.adminId ?? obj?.id ?? obj?.userId ?? obj?.user?.id ?? undefined;
  } catch {
    return undefined;
  }
}

/** Backend sometimes uses envelope: { ok, message, ts, data } */
function unwrapApi(x) {
  if (x && typeof x === "object" && "ok" in x && "data" in x) return x.data;
  return x;
}

function paymentIdFromRow(r) {
  return r?.paymentId ?? r?.id ?? r?.payment_id ?? r?.razorpayPaymentId ?? null;
}

/**
 * Display status mapping:
 * - PAID must be verified (razorpayPaymentId OR paidAt), else UNVERIFIED_PAID
 * - For refunds: FULL/PARTIAL refund should not show "completed" unless refundState is PROCESSED (when present)
 */
function deriveRefundStateFromRow(r) {
  const candidates = [
    r?.refundState,
    r?.refundStatus,
    r?.razorpayRefundStatus,
    r?.latestBookingRefundStatus,
    r?.latestRequestRefundStatus,
  ].map(up);

  return candidates.find((x) => !!x) || "";
}

function derivePaymentDisplayStatusFromRow(r) {
  const base = up(r?.status);
  const hasPaidProof = !!safeStr(r?.razorpayPaymentId) || !!r?.paidAt;
  const refundState = deriveRefundStateFromRow(r);

  if (base === "PAID" && !hasPaidProof) return "UNVERIFIED_PAID";

  if (base === "FULL_REFUND" || base === "PARTIAL_REFUND" || base === "REFUNDED") {
    if (!refundState) return base;
    if (refundState === "PROCESSED") return base;
    if (refundState === "FAILED") return "REFUND_FAILED";
    if (refundState === "PENDING" || refundState === "CREATED") return "REFUND_PENDING";
    return "REFUND_PENDING";
  }

  return base || "—";
}

/* --------------------- status tones (enterprise) --------------------- */
/**
 * ✅ Fix your issue:
 * - PAID = green
 * - FULL_REFUND = blue (NOT green)
 * - PARTIAL_REFUND = amber
 */
function statusTone(st) {
  const s = up(st);

  if (s === "PAID") return "good";
  if (s === "UNVERIFIED_PAID") return "warn";

  if (s === "FULL_REFUND" || s === "REFUNDED") return "info";
  if (s === "PARTIAL_REFUND") return "warn";

  if (s === "REFUND_PENDING") return "warn";
  if (s === "REFUND_FAILED") return "bad";

  if (s === "FAILED") return "bad";
  if (s === "CREATED") return "neutral";

  return "neutral";
}

/* --------------------- refund helpers --------------------- */
function refundTrack(st, hasRefundId) {
  const s = up(st);
  if (!hasRefundId) return { label: "NO REFUND", tone: "neutral", state: "NONE" };
  if (s === "PROCESSED") return { label: "REFUND COMPLETED", tone: "good", state: "COMPLETED" };
  if (s === "PENDING" || s === "CREATED") return { label: "REFUND IN PROGRESS", tone: "warn", state: "IN_PROGRESS" };
  if (s === "FAILED") return { label: "REFUND FAILED", tone: "bad", state: "FAILED" };
  return { label: `REFUND ${s || "UNKNOWN"}`, tone: "neutral", state: "UNKNOWN" };
}

function remainingRefundableGeneric({ paidAmount, refundedAmount, pendingRefundAmount }) {
  const paid = Number(paidAmount ?? 0);
  const refunded = Number(refundedAmount ?? 0);
  const pending = Number(pendingRefundAmount ?? 0);
  return Math.max(0, paid - refunded - pending);
}

/**
 * ✅ Frontend does NOT compute eligibility.
 * It uses backend: refundOps {canIssue, issueWhy, remainingRefundable, ...}
 */
function readRefundOpsFromBackend({ targetKind, booking, request }) {
  const tgt = targetKind === "BOOKING" ? booking : request;
  const ro = tgt?.refundOps || {};

  const targetId =
    targetKind === "BOOKING" ? (booking?.bookingId ?? booking?.id) : (request?.requestId ?? request?.id);

  const refundId = safeStr(targetKind === "BOOKING" ? booking?.razorpayRefundId : request?.razorpayRefundId);
  const refundStatus = safeStr(targetKind === "BOOKING" ? booking?.razorpayRefundStatus : request?.razorpayRefundStatus);

  return {
    targetId,

    canIssue: !!ro?.canIssue,
    issueWhy: safeStr(ro?.issueWhy),

    refundable: Number(ro?.remainingRefundable ?? 0),
    refundId,
    refundStatus,
    track: refundTrack(refundStatus, !!refundId),
  };
}

/* --------------------- UI atoms --------------------- */
function Tag({ label, tone = "neutral", className = "" }) {
  const cls =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : tone === "warn"
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : tone === "bad"
      ? "border-rose-200 bg-rose-50 text-rose-800"
      : tone === "info"
      ? "border-sky-200 bg-sky-50 text-sky-800"
      : "border-slate-200 bg-slate-50 text-slate-700";

  return (
    <span
      className={classNames(
        "inline-flex items-center border px-2 py-1 text-[11px] font-bold uppercase tracking-wider leading-none",
        "rounded-md",
        cls,
        className
      )}
    >
      {label}
    </span>
  );
}

function StatusTag({ status }) {
  const s = up(status);
  return <Tag label={s || "—"} tone={statusTone(s)} />;
}

function Button({ children, onClick, disabled, tone = "soft", className = "", title, type = "button" }) {
  const cls =
    tone === "danger"
      ? "border-rose-700 bg-rose-700 text-white hover:opacity-95"
      : tone === "dark"
      ? "border-slate-900 bg-slate-900 text-white hover:opacity-95"
      : tone === "ghost"
      ? "border-transparent bg-transparent text-slate-700 hover:bg-slate-50"
      : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50";

  return (
    <button
      type={type}
      title={title}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={classNames(
        "inline-flex h-9 items-center justify-center gap-2 border px-3 text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed",
        "rounded-md",
        cls,
        className
      )}
    >
      {children}
    </button>
  );
}

function IconX({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconChevronRight({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconExpand({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path
        d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconCollapse({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path
        d="M3 8h5V3M21 8h-5V3M3 16h5v5M21 16h-5v5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function Section({ title, subtitle, right, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">{title}</div>
          {subtitle ? <div className="mt-1 text-[13px] font-medium text-slate-500">{subtitle}</div> : null}
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-[18px] font-extrabold tracking-tight text-slate-900">{value}</div>
      {sub ? <div className="mt-1 text-[12px] font-medium text-slate-500">{sub}</div> : null}
    </div>
  );
}

/* --------------------- compact mini table --------------------- */
function MiniTable({ rows }) {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      <table className="w-full table-auto">
        <tbody className="divide-y divide-slate-200">
          {rows.map((x, i) => (
            <tr key={i}>
              <td className="w-[42%] bg-slate-50 px-3 py-2 text-[12px] font-bold uppercase tracking-wider text-slate-500">
                {x.k}
              </td>
              <td className="px-3 py-2 text-sm font-semibold text-slate-900">{x.v ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* --------------------- tables in drawer --------------------- */
function SimpleTable({ cols, rows, rowKey, onRowClick }) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200">
      <div className="w-full overflow-x-auto">
        <table className="w-full min-w-[860px] table-auto">
          <thead className="bg-slate-50">
            <tr className="border-b border-slate-200">
              {cols.map((c) => (
                <th
                  key={c.k}
                  className={classNames(
                    "px-4 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500",
                    c.className || ""
                  )}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {rows.map((r) => (
              <tr
                key={rowKey(r)}
                className={classNames(onRowClick ? "cursor-pointer hover:bg-slate-50" : "", "align-middle")}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
              >
                {cols.map((c) => (
                  <td key={c.k} className={classNames("px-4 py-3", c.tdClassName || "")}>
                    {c.render(r)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Tabs({ value, onChange, items }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it) => {
        const active = it.value === value;
        return (
          <button
            key={it.value}
            type="button"
            onClick={() => onChange(it.value)}
            className={classNames(
              "h-9 rounded-md border px-3 text-sm font-extrabold",
              active
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50"
            )}
          >
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

/* --------------------- modal --------------------- */
function ConfirmReasonModal({
  open,
  title,
  subtitle,
  placeholder,
  confirmLabel,
  confirmTone,
  loading,
  onClose,
  onConfirm,
}) {
  const [text, setText] = useState("");

  useEffect(() => {
    if (open) setText("");
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70]">
      <div className="absolute inset-0 bg-black/35" onClick={loading ? undefined : onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white shadow-xl">
          <div className="border-b border-slate-200 px-5 py-4">
            <div className="text-lg font-extrabold tracking-tight text-slate-900">{title}</div>
            {subtitle ? <div className="mt-1 text-[13px] font-medium text-slate-500">{subtitle}</div> : null}
          </div>

          <div className="px-5 py-4">
            <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Reason / Note</div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={placeholder || "Add a clear note for audit trail…"}
              className="mt-2 h-28 w-full resize-none rounded-md border border-slate-200 bg-white p-3 text-sm font-medium text-slate-900 outline-none placeholder:text-slate-400 focus:border-slate-300"
            />

            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button onClick={onClose} disabled={loading} tone="soft">
                Cancel
              </Button>
              <Button
                disabled={loading}
                tone={confirmTone === "danger" ? "danger" : "dark"}
                onClick={() => onConfirm(text)}
              >
                {loading ? "Working…" : confirmLabel}
              </Button>
            </div>

            <div className="mt-3 text-[12px] font-medium text-slate-500">
              Tip: Keep it operator friendly. Example: “Customer complaint → goodwill refund approved.”
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   SupportPaymentsTab
   ========================================================= */
export default function SupportPaymentsTab() {
  const params = useParams();
  const { entityType = "users", entityId } = params;
  const enabled = entityType === "users";
  const userId = entityId ?? params.userId;

  const adminId = useMemo(() => getAdminIdBestEffort(), []);

  // Controls
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);

  // Data
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [res, setRes] = useState({
    items: [],
    summary: null,
    page: { page: 0, size: 25, totalElements: 0, totalPages: 1, hasNext: false, hasPrev: false },
  });

// Drawer
const [drawerOpen, setDrawerOpen] = useState(false);
const [drawerTab, setDrawerTab] = useState("OPERATIONS");


  const [activePaymentId, setActivePaymentId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailErr, setDetailErr] = useState("");
  const [detail, setDetail] = useState(null);

  // Refund ops
  const [refundAction, setRefundAction] = useState(null); // { kind, id, label }
  const [refundBusy, setRefundBusy] = useState(false);
  const [opMsg, setOpMsg] = useState("");

  // Selected target in operations
  const [selectedTarget, setSelectedTarget] = useState(null); // { kind: BOOKING|REQUEST, id }

  const STATUS_OPTIONS = useMemo(() => ["ALL", "CREATED", "PAID", "FAILED", "PARTIAL_REFUND", "FULL_REFUND"], []);

  // lock body scroll when drawer open
  useEffect(() => {
    if (!drawerOpen) return;

    const body = document.body;
    const prevOverflow = body.style.overflow;
    const prevPadRight = body.style.paddingRight;

    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = "hidden";
    if (scrollBarWidth > 0) body.style.paddingRight = `${scrollBarWidth}px`;

    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPadRight;
    };
  }, [drawerOpen]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") {
        if (refundAction) setRefundAction(null);
        else if (drawerOpen) setDrawerOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen, refundAction]);

  async function fetchList({ nextPage = page, nextSize = size, nextQ = q, nextStatus = status } = {}) {
    if (!enabled || !userId) return;

    const effectiveSize = Number(nextSize ?? size ?? 25);

    setLoading(true);
    setErr("");

    try {
      const raw = await listAdminSupportPaymentsForUser({
        userId,
        q: (nextQ || "").trim() || undefined,
        status: nextStatus === "ALL" ? undefined : nextStatus,
        page: nextPage,
        size: effectiveSize,
        adminId,
      });

      const data = unwrapApi(raw) || {};
      const items = Array.isArray(data?.items) ? data.items : Array.isArray(data?.content) ? data.content : [];

      const totalElements = Number(data?.total ?? data?.totalElements ?? items.length ?? 0);
      const totalPages = Math.max(1, Number(data?.totalPages ?? data?.page?.totalPages ?? 1));
      const serverPage = Number(data?.page?.page ?? data?.number ?? nextPage ?? 0);

      const hasPrev = serverPage > 0;
      const hasNext = serverPage + 1 < totalPages;

      setRes({
        items,
        summary: data?.summary ?? null,
        page: {
          page: serverPage,
          size: Number(data?.page?.size ?? effectiveSize ?? 25),
          totalElements: Number.isFinite(totalElements) ? totalElements : 0,
          totalPages,
          hasPrev,
          hasNext,
        },
      });

      if (serverPage !== page) setPage(serverPage);
    } catch (e) {
      setErr(e?.message || "Failed to load payments");
      setRes({
        items: [],
        summary: null,
        page: { page: 0, size: effectiveSize, totalElements: 0, totalPages: 1, hasNext: false, hasPrev: false },
      });
      if (page !== 0) setPage(0);
    } finally {
      setLoading(false);
    }
  }

  async function openDetail(paymentId, { setTab = "OPERATIONS" } = {}) {
    if (!paymentId) return;

    setDrawerOpen(true);
    setActivePaymentId(paymentId);
    setDetail(null);
    setDetailErr("");
    setDetailLoading(true);
    setDrawerTab(setTab);
    setOpMsg("");
    setSelectedTarget(null);

    try {
      const raw = await getAdminSupportPaymentDetail(paymentId, adminId);
      const d = unwrapApi(raw);
      setDetail(d);
    } catch (e) {
      setDetailErr(e?.message || "Failed to load payment detail");
    } finally {
      setDetailLoading(false);
    }
  }

  useEffect(() => {
    if (!enabled) return;
    setQ("");
    setStatus("ALL");
    setPage(0);
    setSize(25);
    fetchList({ nextPage: 0, nextQ: "", nextStatus: "ALL", nextSize: 25 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, userId]);

  useEffect(() => {
    if (!enabled) return;
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, page, size, status]);

  useEffect(() => {
    if (!enabled) return;
    const t = setTimeout(() => {
      setPage(0);
      fetchList({ nextPage: 0 });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // ---------- list derived ----------
  const rows = res.items || [];
  const meta = res.page || {};
  const total = Number(meta.totalElements ?? rows.length ?? 0);
  const totalPages = Math.max(1, Number(meta.totalPages ?? 1));
  const safePage = Math.min(Math.max(0, Number(meta.page ?? page ?? 0)), totalPages - 1);
  const fromIdx = total === 0 ? 0 : safePage * Number(meta.size || size) + 1;
  const toIdx = total === 0 ? 0 : Math.min(total, (safePage + 1) * Number(meta.size || size));

  // ---------- detail derived ----------
  const payment = useMemo(() => detail?.payment ?? detail?.data?.payment ?? null, [detail]);

  const detailBookings = useMemo(() => {
    const arr = detail?.bookings;
    return Array.isArray(arr) ? arr : [];
  }, [detail]);

  const detailRequests = useMemo(() => {
    const arr = detail?.matchRequests;
    return Array.isArray(arr) ? arr : [];
  }, [detail]);

  const summary = res.summary || null;

  const sortedBookings = useMemo(() => {
    if (!detailBookings || detailBookings.length === 0) return [];
    return [...detailBookings].sort((a, b) => Number(b?.bookingId ?? b?.id ?? 0) - Number(a?.bookingId ?? a?.id ?? 0));
  }, [detailBookings]);

  const requestsNoBooking = useMemo(() => {
    if (!detailRequests || detailRequests.length === 0) return [];
    const xs = detailRequests.filter((r) => {
      const fb = Number(r?.finalBookingId ?? 0);
      return !(Number.isFinite(fb) && fb > 0);
    });
    return xs.sort((a, b) => Number(b?.requestId ?? b?.id ?? 0) - Number(a?.requestId ?? a?.id ?? 0));
  }, [detailRequests]);

  const requestsWithFinalBooking = useMemo(() => {
    if (!detailRequests || detailRequests.length === 0) return [];
    const xs = detailRequests.filter((r) => {
      const fb = Number(r?.finalBookingId ?? 0);
      return Number.isFinite(fb) && fb > 0;
    });
    return xs.sort((a, b) => Number(b?.requestId ?? b?.id ?? 0) - Number(a?.requestId ?? a?.id ?? 0));
  }, [detailRequests]);

  // auto-pick target when detail arrives
  useEffect(() => {
    if (!drawerOpen || !payment) return;

    // if user already selected, keep it
    if (selectedTarget?.kind && selectedTarget?.id) return;

    if (sortedBookings.length > 0) {
      const b = sortedBookings[0];
      const id = b?.bookingId ?? b?.id;
      if (id != null) setSelectedTarget({ kind: "BOOKING", id: Number(id) });
      return;
    }

    if (requestsNoBooking.length > 0) {
      const r = requestsNoBooking[0];
      const id = r?.requestId ?? r?.id;
      if (id != null) setSelectedTarget({ kind: "REQUEST", id: Number(id) });
      return;
    }

    setSelectedTarget(null);
  }, [drawerOpen, payment, sortedBookings, requestsNoBooking, selectedTarget]);

  const resolvedTarget = useMemo(() => {
    if (!payment || !selectedTarget) return { kind: null, booking: null, request: null };

    if (selectedTarget.kind === "BOOKING") {
      const found = sortedBookings.find((b) => Number(b?.bookingId ?? b?.id) === Number(selectedTarget.id)) ?? null;
      return { kind: "BOOKING", booking: found, request: null };
    }

    if (selectedTarget.kind === "REQUEST") {
      const found = requestsNoBooking.find((r) => Number(r?.requestId ?? r?.id) === Number(selectedTarget.id)) ?? null;
      return { kind: "REQUEST", booking: null, request: found };
    }

    return { kind: null, booking: null, request: null };
  }, [payment, selectedTarget, sortedBookings, requestsNoBooking]);

  const ops = useMemo(() => {
    if (!payment || !resolvedTarget.kind) return null;
    return readRefundOpsFromBackend({
      targetKind: resolvedTarget.kind,
      booking: resolvedTarget.booking,
      request: resolvedTarget.request,
    });
  }, [payment, resolvedTarget.kind, resolvedTarget.booking, resolvedTarget.request]);

  function openIssueModal() {
    if (!ops || !resolvedTarget.kind) return;
    const label = resolvedTarget.kind === "BOOKING" ? `Booking #${ops.targetId}` : `Request #${ops.targetId}`;
    setRefundAction({ kind: resolvedTarget.kind, id: ops.targetId, label });
  }

  async function issueRefund(kind, id, noteText) {
    setRefundBusy(true);
    setDetailErr("");
    setOpMsg("");
    try {
      if (kind === "BOOKING") {
        const d = await adminSupportIssueRefundForBooking(id, { reason: noteText, adminId });
        setOpMsg(d?.message ? String(d.message) : "Booking refund issued");
      } else {
        const d = await adminSupportIssueRefundForRequest(id, { reason: noteText, adminId });
        setOpMsg(d?.message ? String(d.message) : "Request refund issued");
      }

      // Reload detail so UI stays stable + updated
      if (activePaymentId) {
        await openDetail(activePaymentId, { setTab: "OPERATIONS" });
      }
      setRefundAction(null);
    } catch (e) {
      setDetailErr(e?.message || "Issue failed");
    } finally {
      setRefundBusy(false);
    }
  }

  // ✅ now it is safe to return conditionally (no hooks below this line)
  if (!enabled) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="text-sm font-extrabold text-slate-900">Coming soon</div>
        <div className="mt-1 text-[13px] font-medium text-slate-500">
          Payments support is enabled for <span className="font-semibold text-slate-900">Users</span> in phase-1.
        </div>
      </div>
    );
  }

  // transaction time: prefer paidAt, else createdAt
  function transactionTimeFromRow(r) {
    return r?.paidAt ?? r?.createdAt ?? null;
  }

  function SingleValueCell({ value, onCopy, title }) {
    const v = safeStr(value);
    return (
      <div className="min-w-0">
        <button
          type="button"
          title={title || (v ? "Click to copy" : "")}
          onClick={v && onCopy ? onCopy : undefined}
          disabled={!v || !onCopy}
          className={classNames(
            "w-full text-left",
            v && onCopy ? "cursor-pointer" : "cursor-default"
          )}
        >
          <div className={classNames("truncate text-[13px] font-semibold", v ? "text-slate-900" : "text-slate-400")}>
            {v || "—"}
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-4">
      {/* HEADER + FILTERS */}
      <Section
        title="Payments"
        subtitle="Clean list + full enterprise detail in drawer. Refund is done only from Operations tab."
        right={
          <div className="flex items-center gap-2">
            <div className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 text-sm font-semibold text-slate-900">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">User</span>
              <span className="tabular-nums">#{userId}</span>
            </div>
            <Button tone="soft" disabled={loading} onClick={() => fetchList({ nextPage: safePage, nextSize: size })}>
              Refresh
            </Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-12 lg:items-center">
          <div className="min-w-0 lg:col-span-7">
            <div className="flex h-10 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 focus-within:border-slate-300">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search: paymentId / razorpayOrderId / razorpayPaymentId"
                className="min-w-0 w-full bg-transparent text-sm font-semibold text-slate-900 outline-none placeholder:text-slate-400"
              />
              {q ? (
                <button
                  onClick={() => setQ("")}
                  className="shrink-0 rounded-md border border-slate-200 bg-white p-1 text-slate-600 hover:bg-slate-50"
                  title="Clear"
                  type="button"
                >
                  <IconX className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          </div>

          <div className="min-w-0 lg:col-span-3">
            <div className="relative min-w-0">
              <select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(0);
                }}
                className="h-10 w-full appearance-none rounded-md border border-slate-200 bg-white pl-3 pr-10 text-sm font-semibold text-slate-900 outline-none hover:bg-slate-50 focus:border-slate-300"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s === "ALL" ? "All statuses" : s}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">▾</span>
            </div>
          </div>

          <div className="min-w-0 lg:col-span-2">
            <div className="relative min-w-0">
              <select
                value={String(size)}
                onChange={(e) => {
                  setSize(Number(e.target.value));
                  setPage(0);
                }}
                className="h-10 w-full appearance-none rounded-md border border-slate-200 bg-white pl-3 pr-10 text-sm font-semibold text-slate-900 outline-none hover:bg-slate-50 focus:border-slate-300"
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}/page
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">▾</span>
            </div>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <div className="text-[13px] font-medium text-slate-500">
            {loading ? "Loading…" : `${rows.length} shown • ${total} total • ${fromIdx}-${toIdx}`}
          </div>
          <div className="text-[12px] font-medium text-slate-400">Tip: Open View → use Operations tab for refunds.</div>
        </div>

        {err ? (
          <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-semibold text-rose-700">
            {err}
          </div>
        ) : null}
      </Section>

      {/* SUMMARY */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Total paid" value={fmtINR(summary?.totalPaid)} sub={`${Number(summary?.paidCount ?? 0)} payments`} />
        <StatCard
          label="Total refunded"
          value={fmtINR(summary?.totalRefunded)}
          sub={`${Number(summary?.refundCount ?? 0)} refunds`}
        />
        <StatCard label="Net collected" value={fmtINR(summary?.netCollected)} sub="Paid - Refunded" />
        <StatCard label="All payments" value={String(summary?.paymentCount ?? total ?? 0)} sub={`${Number(summary?.failedCount ?? 0)} failed`} />
      </div>

      {/* TABLE (clean, single-value cells) */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-5 py-3">
          <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Payments list</div>
        </div>

        {loading ? (
          <div className="px-5 py-10 text-sm font-semibold text-slate-600">Loading payments…</div>
        ) : rows.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <div className="text-sm font-semibold text-slate-900">No payments found</div>
            <div className="mt-1 text-[13px] font-medium text-slate-500">
              If bookings exist but this is empty, those bookings may not have a saved payment record / paymentId yet.
            </div>
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <table className="w-full min-w-[1480px] table-auto">
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500 min-w-[170px]">
                    Payment ID
                  </th>
                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500 min-w-[200px]">
                    Transaction time
                  </th>
                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500 min-w-[160px]">
                    Status
                  </th>
                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500 min-w-[170px]">
                    Paid (INR)
                  </th>
                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500 min-w-[170px]">
                    Refunded (INR)
                  </th>
                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500 min-w-[120px]">
                    Bookings
                  </th>
                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500 min-w-[120px]">
                    Requests
                  </th>
                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500 min-w-[240px]">
                    Razorpay order ID
                  </th>
                  <th className="px-5 py-3 text-left text-[12px] font-bold uppercase tracking-wider text-slate-500 min-w-[240px]">
                    Razorpay payment ID
                  </th>
                  <th className="px-5 py-3 text-right text-[12px] font-bold uppercase tracking-wider text-slate-500 min-w-[120px]">
                    Open
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200">
                {rows.map((r, idx) => {
                  const pid = paymentIdFromRow(r) ?? `row-${idx}`;
                  const tx = fmtDT(transactionTimeFromRow(r));
                  const displayStatus = derivePaymentDisplayStatusFromRow(r);

                  const bookingsCount = Number(r?.bookingsCount ?? 0);
                  const requestsCount = Number(r?.matchRequestsCount ?? 0);

                  const rpOrderId = safeStr(r?.razorpayOrderId);
                  const rpPaymentId = safeStr(r?.razorpayPaymentId);

                  return (
                    <tr key={String(pid)} className="hover:bg-slate-50">
                      <td className="px-5 py-4 align-middle">
                        <div className="whitespace-nowrap tabular-nums text-[13px] font-extrabold text-slate-900">
                          {pid ? `#${pid}` : "—"}
                        </div>
                      </td>

                      <td className="px-5 py-4 align-middle" title={tx.rel || ""}>
                        <div className="whitespace-nowrap text-[13px] font-semibold text-slate-900">{tx.pretty}</div>
                      </td>

                      <td className="px-5 py-4 align-middle">
                        <StatusTag status={displayStatus} />
                      </td>

                      <td className="px-5 py-4 align-middle">
                        <div className="whitespace-nowrap tabular-nums text-[13px] font-semibold text-slate-900">
                          {fmtINR(r?.amount)}
                        </div>
                      </td>

                      <td className="px-5 py-4 align-middle">
                        <div className="whitespace-nowrap tabular-nums text-[13px] font-semibold text-slate-900">
                          {fmtINR(r?.refundedAmount)}
                        </div>
                      </td>

                      <td className="px-5 py-4 align-middle">
                        <div className="whitespace-nowrap tabular-nums text-[13px] font-semibold text-slate-900">
                          {Number.isFinite(bookingsCount) ? bookingsCount : 0}
                        </div>
                      </td>

                      <td className="px-5 py-4 align-middle">
                        <div className="whitespace-nowrap tabular-nums text-[13px] font-semibold text-slate-900">
                          {Number.isFinite(requestsCount) ? requestsCount : 0}
                        </div>
                      </td>

                      <td className="px-5 py-4 align-middle">
                        <SingleValueCell
                          value={rpOrderId}
                          onCopy={rpOrderId ? () => copyText(rpOrderId) : null}
                          title={rpOrderId ? "Click to copy Razorpay order ID" : ""}
                        />
                      </td>

                      <td className="px-5 py-4 align-middle">
                        <SingleValueCell
                          value={rpPaymentId}
                          onCopy={rpPaymentId ? () => copyText(rpPaymentId) : null}
                          title={rpPaymentId ? "Click to copy Razorpay payment ID" : ""}
                        />
                      </td>

                      <td className="px-5 py-4 align-middle text-right">
                        <button
                          type="button"
                          onClick={() => openDetail(paymentIdFromRow(r))}
                          disabled={!paymentIdFromRow(r)}
                          className={classNames(
                            "inline-flex h-10 items-center justify-center gap-2 rounded-md border px-3 text-sm font-semibold",
                            !paymentIdFromRow(r)
                              ? "border-slate-200 bg-white text-slate-400 cursor-not-allowed"
                              : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50"
                          )}
                        >
                          View <IconChevronRight className="h-4 w-4 text-slate-500" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination (simple + stable) */}
        <div className="flex flex-col gap-3 border-t border-slate-200 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="text-[13px] font-medium text-slate-500">
            Showing <span className="font-semibold text-slate-900 tabular-nums">{fromIdx}-{toIdx}</span> of{" "}
            <span className="font-semibold text-slate-900 tabular-nums">{total}</span> • Page{" "}
            <span className="font-semibold text-slate-900 tabular-nums">{safePage + 1}</span> /{" "}
            <span className="font-semibold text-slate-900 tabular-nums">{totalPages}</span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              disabled={loading || safePage <= 0 || !meta?.hasPrev}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              tone="soft"
            >
              Prev
            </Button>
            <Button
              disabled={loading || !meta?.hasNext || safePage >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
              tone="soft"
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {/* DRAWER */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[60]">
          <div className="absolute inset-0 bg-black/25" onClick={() => setDrawerOpen(false)} />
        <div className="absolute right-0 top-0 h-full w-full max-w-5xl overflow-y-auto border-l border-slate-200 bg-white shadow-2xl">

            {/* Header */}
            <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur px-5 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Payment detail</div>
                  <div className="mt-1 truncate text-2xl font-extrabold tracking-tight text-slate-900">
                    Payment <span className="tabular-nums">#{activePaymentId}</span>
                  </div>

                  {payment ? (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <StatusTag status={derivePaymentDisplayStatusFromRow(payment)} />
                      {payment?.razorpayOrderId ? <Tag label="RP ORDER" /> : null}
                      {payment?.razorpayPaymentId ? <Tag label="RP PAYMENT" /> : null}
                      {payment?.userId ? <Tag label={`USER ${payment.userId}`} /> : null}
                      {sortedBookings.length > 0 ? <Tag label={`${sortedBookings.length} BOOKINGS`} tone="good" /> : <Tag label="0 BOOKINGS" />}
                      {detailRequests.length > 0 ? <Tag label={`${detailRequests.length} REQUESTS`} tone="neutral" /> : <Tag label="0 REQUESTS" />}
                    </div>
                  ) : null}
                </div>

              <div className="flex gap-2">
  <Button onClick={() => setDrawerOpen(false)} tone="soft">
    Close
  </Button>
</div>

              </div>

              {detailErr ? (
                <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-semibold text-rose-700">
                  {detailErr}
                </div>
              ) : null}

              {/* Tabs */}
              <div className="mt-4">
                <Tabs
                  value={drawerTab}
                  onChange={setDrawerTab}
                  items={[
                    { value: "OPERATIONS", label: "Operations" },
                    { value: "OVERVIEW", label: "Overview" },
                    { value: "REFERENCES", label: "References" },
                    { value: "RAW", label: "Raw" },
                  ]}
                />
              </div>
            </div>

            <div className="space-y-4 px-5 py-4">
              {detailLoading ? (
                <div className="rounded-xl border border-slate-200 bg-white p-5 text-sm font-semibold text-slate-600">
                  Loading detail…
                </div>
              ) : !payment ? (
                <div className="rounded-xl border border-slate-200 bg-white p-5 text-sm font-semibold text-slate-600">
                  No detail loaded.
                </div>
              ) : (
                <>
                  {/* OPERATIONS TAB */}
                  {drawerTab === "OPERATIONS" ? (
                    <div className="space-y-4">
                      <Section
                        title="Refund operations"
                        subtitle="Single clean action: Issue Refund (backend controls enable/disable). Select target from References tab or from lists below."
                        right={
                          <div className="flex items-center gap-2">
                            <Button
                              tone="soft"
                              disabled={detailLoading}
                              onClick={() => openDetail(activePaymentId, { setTab: "OPERATIONS" })}
                            >
                              Reload
                            </Button>
                          </div>
                        }
                      >
                        <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
                          {/* Left: selection */}
                          <div className="xl:col-span-7 space-y-3">
                            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <Tag label="TARGET" />
                                  {resolvedTarget.kind ? (
                                    <>
                                      <Tag
                                        label={resolvedTarget.kind}
                                        tone={resolvedTarget.kind === "BOOKING" ? "good" : "neutral"}
                                      />
                                      <span className="text-[14px] font-extrabold text-slate-900 tabular-nums">
                                        #{ops?.targetId ?? "—"}
                                      </span>
                                      {ops ? <Tag label={ops.track.label} tone={ops.track.tone} /> : null}
                                    </>
                                  ) : (
                                    <span className="text-[13px] font-semibold text-slate-600">No refundable target available</span>
                                  )}
                                </div>

                                {ops ? (
                                  <div className="text-[12px] font-medium text-slate-600">
                                    Remaining:{" "}
                                    <span className="font-extrabold text-slate-900 tabular-nums">{fmtINR(ops.refundable)}</span>
                                  </div>
                                ) : null}
                              </div>

                              <div className="mt-3 text-[12px] font-medium text-slate-600">
                                Selection is stable & explicit. Click a Booking/Request row below (or in References tab) to set the refund target.
                              </div>
                            </div>

                            {/* Quick selector lists (not dropdowns) */}
                            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                              <div className="rounded-xl border border-slate-200 bg-white p-4">
                                <div className="flex items-center justify-between">
                                  <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Bookings</div>
                                  <Tag label={String(sortedBookings.length)} tone={sortedBookings.length ? "good" : "neutral"} />
                                </div>

                                {sortedBookings.length === 0 ? (
                                  <div className="mt-3 text-sm font-medium text-slate-500">No bookings linked.</div>
                                ) : (
                                  <div className="mt-3 space-y-2 max-h-[260px] overflow-auto pr-1">
                                    {sortedBookings.map((b) => {
                                      const id = b?.bookingId ?? b?.id;
                                      const active = selectedTarget?.kind === "BOOKING" && Number(selectedTarget?.id) === Number(id);
                                      const track = refundTrack(b?.razorpayRefundStatus, !!b?.razorpayRefundId);
                                      return (
                                        <button
                                          key={String(id)}
                                          type="button"
                                          onClick={() => {
                                            setSelectedTarget({ kind: "BOOKING", id: Number(id) });
                                            setOpMsg("");
                                          }}
                                          className={classNames(
                                            "w-full rounded-lg border p-3 text-left",
                                            active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:bg-slate-50"
                                          )}
                                        >
                                          <div className="flex flex-wrap items-center justify-between gap-2">
                                            <div className="flex items-center gap-2">
                                              <span
                                                className={classNames(
                                                  "text-[13px] font-extrabold tabular-nums",
                                                  active ? "text-white" : "text-slate-900"
                                                )}
                                              >
                                                #{id}
                                              </span>
                                              <span className={classNames("text-[12px] font-semibold", active ? "text-white/85" : "text-slate-600")}>
                                                {up(b?.bookingStatus) || "—"}
                                              </span>
                                            </div>
                                            <Tag label={track.label} tone={active ? "neutral" : track.tone} />
                                          </div>
                                          <div className={classNames("mt-2 text-[12px] font-medium", active ? "text-white/75" : "text-slate-500")}>
                                            Paid: <span className="font-semibold">{fmtMoney(b?.paidAmount)}</span> • Refunded:{" "}
                                            <span className="font-semibold">{fmtMoney(b?.refundedAmount)}</span>
                                          </div>
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>

                              <div className="rounded-xl border border-slate-200 bg-white p-4">
                                <div className="flex items-center justify-between">
                                  <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">
                                    Requests (no final booking)
                                  </div>
                                  <Tag label={String(requestsNoBooking.length)} tone={requestsNoBooking.length ? "neutral" : "neutral"} />
                                </div>

                                {requestsNoBooking.length === 0 ? (
                                  <div className="mt-3 text-sm font-medium text-slate-500">No requests eligible (no-booking) for refund.</div>
                                ) : (
                                  <div className="mt-3 space-y-2 max-h-[260px] overflow-auto pr-1">
                                    {requestsNoBooking.map((r) => {
                                      const id = r?.requestId ?? r?.id;
                                      const active = selectedTarget?.kind === "REQUEST" && Number(selectedTarget?.id) === Number(id);
                                      const track = refundTrack(r?.razorpayRefundStatus, !!r?.razorpayRefundId);
                                      return (
                                        <button
                                          key={String(id)}
                                          type="button"
                                          onClick={() => {
                                            setSelectedTarget({ kind: "REQUEST", id: Number(id) });
                                            setOpMsg("");
                                          }}
                                          className={classNames(
                                            "w-full rounded-lg border p-3 text-left",
                                            active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:bg-slate-50"
                                          )}
                                        >
                                          <div className="flex flex-wrap items-center justify-between gap-2">
                                            <div className="flex items-center gap-2">
                                              <span
                                                className={classNames(
                                                  "text-[13px] font-extrabold tabular-nums",
                                                  active ? "text-white" : "text-slate-900"
                                                )}
                                              >
                                                #{id}
                                              </span>
                                              <span className={classNames("text-[12px] font-semibold", active ? "text-white/85" : "text-slate-600")}>
                                                {up(r?.status) || "—"}
                                              </span>
                                            </div>
                                            <Tag label={track.label} tone={active ? "neutral" : track.tone} />
                                          </div>
                                          <div className={classNames("mt-2 text-[12px] font-medium", active ? "text-white/75" : "text-slate-500")}>
                                            Paid: <span className="font-semibold">{fmtMoney(r?.paidAmount)}</span> • Refunded:{" "}
                                            <span className="font-semibold">{fmtMoney(r?.refundedAmount)}</span>
                                          </div>
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Right: action */}
                          <div className="xl:col-span-5 space-y-3">
                            <div className="rounded-xl border border-slate-200 bg-white p-4">
                              <div className="flex items-center justify-between gap-2">
                                <div>
                                  <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Action</div>
                                  <div className="mt-1 text-sm font-extrabold text-slate-900">Issue refund</div>
                                </div>
                                {ops ? <Tag label={ops.track.label} tone={ops.track.tone} /> : <Tag label="NO TARGET" />}
                              </div>

                              <div className="mt-3">
                                <Button
                                  tone="danger"
                                  disabled={!ops || !ops.canIssue || refundBusy}
                                  title={!ops ? "No target available." : ops.canIssue ? "" : ops.issueWhy}
                                  onClick={openIssueModal}
                                  className="w-full h-10"
                                >
                                  {refundBusy ? "Working…" : "Issue refund"}
                                </Button>
                              </div>

                              <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-3">
                                <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Backend decision</div>
                                <div className="mt-2 text-[13px] font-semibold text-slate-900">
                                  {ops ? (ops.canIssue ? "Enabled" : "Disabled") : "No target"}
                                </div>
                                <div className="mt-1 text-[12px] font-medium text-slate-600">
                                  {ops
                                    ? ops.canIssue
                                      ? "You can issue refund now."
                                      : ops.issueWhy || "Disabled by policy."
                                    : "No bookings and no eligible requests."}
                                </div>
                              </div>

                              {opMsg ? (
                                <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-3 text-[13px] font-semibold text-emerald-800">
                                  {opMsg}
                                </div>
                              ) : null}
                            </div>

                            <div className="rounded-xl border border-slate-200 bg-white p-4">
                              <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Target snapshot</div>
                              {!ops ? (
                                <div className="mt-2 text-sm font-medium text-slate-500">No target</div>
                              ) : (
                                <div className="mt-3">
                                  <MiniTable
                                    rows={[
                                      { k: "Target", v: `${resolvedTarget.kind} #${ops.targetId}` },
                                      { k: "Refundable remaining", v: fmtINR(ops.refundable) },
                                      { k: "Refund ID", v: ops.refundId ? <span className="break-all">{ops.refundId}</span> : "—" },
                                      { k: "Refund status", v: up(ops.refundStatus) || "—" },
                                    ]}
                                  />
                                </div>
                              )}
                            </div>

                            <div className="rounded-xl border border-slate-200 bg-white p-4">
                              <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Why no Preview / Refresh?</div>
                              <div className="mt-2 text-[12px] font-medium text-slate-600">
                                You asked to remove them. Status becomes stable via reloads after issue and regular detail reload.
                              </div>
                            </div>
                          </div>
                        </div>
                      </Section>

                      {/* Requests with final booking info (for clarity) */}
                      {requestsWithFinalBooking.length > 0 ? (
                        <Section
                          title="Requests with final booking"
                          subtitle="Refund for these requests must be done via the final booking. This section is informational."
                        >
                          <SimpleTable
                            cols={[
                              {
                                k: "id",
                                label: "Request",
                                render: (r) => (
                                  <div className="flex items-center gap-2">
                                    <Tag label="REQUEST" />
                                    <span className="text-[13px] font-extrabold tabular-nums text-slate-900">
                                      #{r?.requestId ?? r?.id}
                                    </span>
                                  </div>
                                ),
                              },
                              { k: "st", label: "Status", render: (r) => <Tag label={up(r?.status) || "—"} tone="neutral" /> },
                              {
                                k: "fb",
                                label: "Final booking",
                                render: (r) => {
                                  const fb = r?.finalBookingId;
                                  return fb ? <Tag label={`BOOKING #${fb}`} tone="good" /> : "—";
                                },
                              },
                            ]}
                            rows={requestsWithFinalBooking}
                            rowKey={(r) => String(r?.requestId ?? r?.id)}
                            onRowClick={(r) => {
                              const fb = Number(r?.finalBookingId ?? 0);
                              if (fb > 0) {
                                setSelectedTarget({ kind: "BOOKING", id: fb });
                                setOpMsg("");
                                setDrawerTab("OPERATIONS");
                              }
                            }}
                          />
                        </Section>
                      ) : null}
                    </div>
                  ) : null}

                  {/* OVERVIEW TAB */}
                  {drawerTab === "OVERVIEW" ? (
                    <Section title="Payment overview" subtitle="Identifiers, money, and Razorpay references.">
                      {(() => {
                        const created = fmtDT(payment.createdAt);
                        const paidAt = fmtDT(payment.paidAt);

                        const rpOrder = safeStr(payment.razorpayOrderId);
                        const rpPay = safeStr(payment.razorpayPaymentId);

                        return (
                          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                            <div className="rounded-xl border border-slate-200 bg-white p-4">
                              <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Money</div>
                              <div className="mt-3 space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Amount</span>
                                  <span className="text-[14px] font-extrabold text-slate-900 tabular-nums">{fmtINR(payment.amount)}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Refunded</span>
                                  <span className="text-[13px] font-extrabold text-slate-900 tabular-nums">{fmtINR(payment.refundedAmount)}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Status</span>
                                  <StatusTag status={derivePaymentDisplayStatusFromRow(payment)} />
                                </div>
                              </div>
                            </div>

                            <div className="rounded-xl border border-slate-200 bg-white p-4">
                              <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">User</div>
                              <div className="mt-3 space-y-2">
                                <div className="flex items-center justify-between gap-3">
                                  <span className="text-[12px] font-bold uppercase tracking-wider text-slate-500">User ID</span>
                                  <span className="text-sm font-extrabold text-slate-900 tabular-nums">{payment.userId ?? userId ?? "—"}</span>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                  <span className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Phone</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-semibold text-slate-900">{payment.userPhone ?? "—"}</span>
                                    {payment.userPhone ? (
                                      <Button tone="soft" onClick={() => copyText(payment.userPhone)} className="h-8 px-2 text-[12px]">
                                        Copy
                                      </Button>
                                    ) : null}
                                  </div>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                  <span className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Email</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-semibold text-slate-900 break-all">{payment.userEmail ?? "—"}</span>
                                    {payment.userEmail ? (
                                      <Button tone="soft" onClick={() => copyText(payment.userEmail)} className="h-8 px-2 text-[12px]">
                                        Copy
                                      </Button>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="rounded-xl border border-slate-200 bg-white p-4">
                              <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Razorpay</div>
                              <div className="mt-3 space-y-2">
                                <div className="flex items-start justify-between gap-3">
                                  <span className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Order ID</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-semibold text-slate-900 break-all">{rpOrder || "—"}</span>
                                    {rpOrder ? (
                                      <Button tone="soft" onClick={() => copyText(rpOrder)} className="h-8 px-2 text-[12px]">
                                        Copy
                                      </Button>
                                    ) : null}
                                  </div>
                                </div>
                                <div className="flex items-start justify-between gap-3">
                                  <span className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Payment ID</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-semibold text-slate-900 break-all">{rpPay || "—"}</span>
                                    {rpPay ? (
                                      <Button tone="soft" onClick={() => copyText(rpPay)} className="h-8 px-2 text-[12px]">
                                        Copy
                                      </Button>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="rounded-xl border border-slate-200 bg-white p-4">
                              <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Times</div>
                              <div className="mt-3 space-y-2">
                                <div className="flex items-center justify-between gap-3">
                                  <span className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Created</span>
                                  <span className="text-sm font-semibold text-slate-900">{created.pretty}</span>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                  <span className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Paid</span>
                                  <span className="text-sm font-semibold text-slate-900">{paidAt.pretty}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </Section>
                  ) : null}

                  {/* REFERENCES TAB */}
                  {drawerTab === "REFERENCES" ? (
                    <div className="space-y-4">
                      <Section
                        title="Bookings"
                        subtitle="All booking IDs and statuses for this payment. Click a row to set refund target."
                      >
                        {sortedBookings.length === 0 ? (
                          <div className="text-sm font-medium text-slate-500">No bookings linked.</div>
                        ) : (
                          <SimpleTable
                            cols={[
                              {
                                k: "id",
                                label: "Booking",
                                render: (b) => (
                                  <div className="flex items-center gap-2">
                                    <Tag label="BOOKING" tone="good" />
                                    <span className="text-[13px] font-extrabold tabular-nums text-slate-900">
                                      #{b?.bookingId ?? b?.id}
                                    </span>
                                  </div>
                                ),
                              },
                              { k: "st", label: "Status", render: (b) => <Tag label={up(b?.bookingStatus) || "—"} /> },
                              {
                                k: "amt",
                                label: "Amounts",
                                render: (b) => (
                                  <div className="text-[12px] font-semibold text-slate-900">
                                    Paid: <span className="tabular-nums">{fmtMoney(b?.paidAmount)}</span>
                                    <span className="mx-2 text-slate-400">•</span>
                                    Refunded: <span className="tabular-nums">{fmtMoney(b?.refundedAmount)}</span>
                                  </div>
                                ),
                              },
                              {
                                k: "ref",
                                label: "Refund",
                                render: (b) => {
                                  const tr = refundTrack(b?.razorpayRefundStatus, !!b?.razorpayRefundId);
                                  return <Tag label={tr.label} tone={tr.tone} />;
                                },
                              },
                            ]}
                            rows={sortedBookings}
                            rowKey={(b) => String(b?.bookingId ?? b?.id)}
                            onRowClick={(b) => {
                              const id = b?.bookingId ?? b?.id;
                              if (id == null) return;
                              setSelectedTarget({ kind: "BOOKING", id: Number(id) });
                              setOpMsg("");
                              setDrawerTab("OPERATIONS");
                            }}
                          />
                        )}
                      </Section>

                      <Section
                        title="Requests (no final booking)"
                        subtitle="These requests have no final booking. Click a row to set refund target."
                      >
                        {requestsNoBooking.length === 0 ? (
                          <div className="text-sm font-medium text-slate-500">None.</div>
                        ) : (
                          <SimpleTable
                            cols={[
                              {
                                k: "id",
                                label: "Request",
                                render: (r) => (
                                  <div className="flex items-center gap-2">
                                    <Tag label="REQUEST" />
                                    <span className="text-[13px] font-extrabold tabular-nums text-slate-900">
                                      #{r?.requestId ?? r?.id}
                                    </span>
                                  </div>
                                ),
                              },
                              { k: "st", label: "Status", render: (r) => <Tag label={up(r?.status) || "—"} /> },
                              {
                                k: "amt",
                                label: "Amounts",
                                render: (r) => (
                                  <div className="text-[12px] font-semibold text-slate-900">
                                    Paid: <span className="tabular-nums">{fmtMoney(r?.paidAmount)}</span>
                                    <span className="mx-2 text-slate-400">•</span>
                                    Refunded: <span className="tabular-nums">{fmtMoney(r?.refundedAmount)}</span>
                                  </div>
                                ),
                              },
                              {
                                k: "ref",
                                label: "Refund",
                                render: (r) => {
                                  const tr = refundTrack(r?.razorpayRefundStatus, !!r?.razorpayRefundId);
                                  return <Tag label={tr.label} tone={tr.tone} />;
                                },
                              },
                            ]}
                            rows={requestsNoBooking}
                            rowKey={(r) => String(r?.requestId ?? r?.id)}
                            onRowClick={(r) => {
                              const id = r?.requestId ?? r?.id;
                              if (id == null) return;
                              setSelectedTarget({ kind: "REQUEST", id: Number(id) });
                              setOpMsg("");
                              setDrawerTab("OPERATIONS");
                            }}
                          />
                        )}
                      </Section>

                      {requestsWithFinalBooking.length > 0 ? (
                        <Section
                          title="Requests (has final booking)"
                          subtitle="Refund must be issued via final booking. Click row to jump to that booking target."
                        >
                          <SimpleTable
                            cols={[
                              {
                                k: "id",
                                label: "Request",
                                render: (r) => (
                                  <div className="flex items-center gap-2">
                                    <Tag label="REQUEST" />
                                    <span className="text-[13px] font-extrabold tabular-nums text-slate-900">
                                      #{r?.requestId ?? r?.id}
                                    </span>
                                  </div>
                                ),
                              },
                              { k: "st", label: "Status", render: (r) => <Tag label={up(r?.status) || "—"} /> },
                              {
                                k: "fb",
                                label: "Final booking",
                                render: (r) => <Tag label={`BOOKING #${r?.finalBookingId}`} tone="good" />,
                              },
                            ]}
                            rows={requestsWithFinalBooking}
                            rowKey={(r) => String(r?.requestId ?? r?.id)}
                            onRowClick={(r) => {
                              const fb = Number(r?.finalBookingId ?? 0);
                              if (fb > 0) {
                                setSelectedTarget({ kind: "BOOKING", id: fb });
                                setOpMsg("");
                                setDrawerTab("OPERATIONS");
                              }
                            }}
                          />
                        </Section>
                      ) : null}
                    </div>
                  ) : null}

                  {/* RAW TAB */}
                  {drawerTab === "RAW" ? (
                    <div className="rounded-xl border border-slate-200 bg-white">
                      <div className="border-b border-slate-200 px-5 py-4">
                        <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Raw payload</div>
                        <div className="mt-1 text-[13px] font-medium text-slate-500">Use only when debugging.</div>
                      </div>
                      <div className="px-5 py-4">
                        <pre className="max-h-[520px] overflow-auto rounded-md border border-slate-200 bg-slate-50 p-3 text-[12px] font-medium text-slate-700">
                          {JSON.stringify(detail, null, 2)}
                        </pre>
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmReasonModal
        open={!!refundAction}
        title="Issue refund"
        subtitle={
          refundAction
            ? `This will issue a Razorpay refund for ${refundAction.label}. Add a clear reason for audit trail.`
            : ""
        }
        confirmLabel="Confirm issue refund"
        confirmTone="danger"
        loading={refundBusy}
        onClose={() => setRefundAction(null)}
        onConfirm={(note) => issueRefund(refundAction.kind, refundAction.id, note)}
      />
    </div>
  );
}
